'use client';

import React, { useState, useCallback } from 'react';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { IResortLayout } from '@/page-components/resort/_layout';
import { fetchResortReports } from '@/api/resort/static';
import {
  IFirstHandReport, IResortReports, IResortPathInfo, IResortPageInfo,
} from '@/api/resort/types';
import { Main } from '@/components/resort/Layout/Main';
import { Side } from '@/components/resort/Layout/Side';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { Hero400Error } from '@/components/400';
import { SidebarAd } from '@/tracking/ads/components/SidebarAd';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';
import { PageBody } from '@/components/common/PageBody';
import { SeoSection } from '@/components/common/SeoSection';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { formatAgoTimestampOrString } from '@/util/formatAgo';
import { Diagnostics } from '@/util/diagnostics';
import { useAnalytics } from '@/tracking/analytics';

export type ResortFHPageProps = {
  resort: IResortPageInfo,
  pathInfo: IResortPathInfo,
  resortReports: IResortReports | null,
} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-firsthand-page';
export const TEMPLATE_ID = '2.2.4';
export const TEMPLATE_NAME = 'Resort Ski Report';

const PAGE_SIZE = 10;
const TRUNCATE_AT = 200;

// "Sarah K" / "Sarah K." / "Sarah" → "SK" / "SK" / "S".
const initialsFor = (name?: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join('') || '?';
};

const ReportCard = ({ report }: { report: IFirstHandReport }) => {
  const { t } = useOTSTranslation('common');
  const [expanded, setExpanded] = useState(false);
  const body = report.translated || report.body || '';
  const isTruncated = body.length > TRUNCATE_AT;
  const visible = expanded || !isTruncated
    ? body
    : `${body.slice(0, TRUNCATE_AT).trimEnd()}…`;

  if (!body && !report.image) return null;

  return (
    <article className="border-b border-border pb-8 last:border-b-0 last:pb-0">
      <div className="mx-auto max-w-2xl">
        <div className="mb-3 flex items-center gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
            {initialsFor(report.name)}
          </span>
          <div>
            <div className="text-sm font-semibold text-foreground">
              {report.name || t('common.anonymous')}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatAgoTimestampOrString(report.date)}
            </div>
          </div>
        </div>

        {report.image && (
          <div className="mb-4 aspect-[4/5] w-full overflow-hidden rounded-md bg-muted shadow-sm">
            <img
              src={report.largeImage || report.image}
              alt={report.title || t('resort.firsthand.reportPhotoAlt', { name: report.name || t('common.anonymous') })}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </div>
        )}

        {body && (
          <Text variant="default">
            {visible}
            {isTruncated && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="font-semibold text-primary hover:underline"
                >
                  {expanded ? t('resort.overview.showLess') : t('resort.overview.readMore')}
                </button>
              </>
            )}
          </Text>
        )}
      </div>
    </article>
  );
};

export default function ResortFHPage({
  resort, pathInfo, resortReports,
}: ResortFHPageProps) {
  const { t } = useOTSTranslation('common');
  const a = useAnalytics();

  const [pages, setPages] = useState<IResortReports[]>(
    resortReports ? [resortReports] : [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const loadMore = useCallback(async () => {
    if (isLoading) return;
    a.viewContent({ content: 'First Hand Reports' });
    setIsLoading(true);
    const nextPage = currentPage + 1;
    const next = await fetchResortReports(pathInfo.uuid, nextPage, PAGE_SIZE);
    if (!next || !next.pagination || !next.reportsList) {
      Diagnostics.error({
        message: 'Server returned unknown report page type',
        tag: 'resort-reports',
      });
      setIsLoading(false);
      return;
    }
    setPages((prev) => [...prev, next]);
    setCurrentPage(nextPage);
    setIsLoading(false);
  }, [a, currentPage, isLoading, pathInfo.uuid]);

  if (!resort) {
    return <Hero400Error light />;
  }

  const totalCount = pages[0]?.pagination?.count ?? 0;
  const loadedCount = pages.reduce((sum, p) => sum + (p.reportsList?.length ?? 0), 0);
  const remaining = Math.max(0, totalCount - loadedCount);
  const hasReports = loadedCount > 0;

  return (
    <>
      <Main>
        <PageBody spacing="md">
          <SeoSection title={t('resort.fhr.seoTitle', { resort: resort.title })}>
            {t('resort.fhr.seoText', { resort: resort.title })}
          </SeoSection>

          {hasReports ? (
            <section className="mb-12">
              <div className="mb-6 flex items-baseline justify-between gap-3">
                <Heading level={2}>
                  {t('resort.firsthand.heading')}
                </Heading>
                <Text variant="subtle" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('resort.firsthand.totalCount', { count: totalCount })}
                </Text>
              </div>

              <div className="space-y-8">
                {pages.flatMap((p) => p.reportsList).map((report) => (
                  <ReportCard
                    key={report.uuid}
                    report={report}
                  />
                ))}
              </div>

              {remaining > 0 && (
                <div className="mt-8 flex justify-center">
                  <Button
                    type="button"
                    size="lg"
                    variant="outline"
                    onClick={loadMore}
                    disabled={isLoading}
                    className="rounded-full px-7 text-xs font-bold uppercase tracking-widest"
                  >
                    {isLoading ? '…' : t('common.loadMore')}
                    {!isLoading && (
                      <span className="ml-1.5 text-muted-foreground">
                        {`· ${t('resort.firsthand.remaining', { count: remaining })}`}
                      </span>
                    )}
                  </Button>
                </div>
              )}
            </section>
          ) : (
            <Text variant="muted">
              {t('common.na')}
            </Text>
          )}

          <SkiGptPromotion
            templateId={TEMPLATE_ID}
            name={resort.title}
            resortId={pathInfo.uuid}
          />
        </PageBody>

      </Main>

      <Side>
        <SidebarAd id={pathInfo.uuid} />
      </Side>
    </>
  );
}

// fetchProps moved to ./firsthand.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
